/**
 * Storage Service for 1946 Billing PWA
 * High-level API for offline data management
 */

import localDB from '../utils/db';

const StorageService = {
  /**
   * Initialize local database
   */
  async init() {
    try {
      await localDB.init();
      console.log('[Storage] Initialized successfully');
      return true;
    } catch (error) {
      console.error('[Storage] Initialization failed:', error);
      return false;
    }
  },

  // ==================== MENU ITEMS ====================

  /**
   * Save menu items to local storage
   */
  async saveMenuItems(items) {
    try {
      await localDB.bulkPut('menu_items', items);
      console.log(`[Storage] Saved ${items.length} menu items`);
      return true;
    } catch (error) {
      console.error('[Storage] Error saving menu items:', error);
      return false;
    }
  },

  /**
   * Get all menu items from local storage
   */
  async getMenuItems() {
    try {
      const items = await localDB.getAll('menu_items');
      console.log(`[Storage] Retrieved ${items.length} menu items`);
      return items;
    } catch (error) {
      console.error('[Storage] Error getting menu items:', error);
      return [];
    }
  },

  /**
   * Get menu item by ID
   */
  async getMenuItem(id) {
    try {
      return await localDB.get('menu_items', id);
    } catch (error) {
      console.error('[Storage] Error getting menu item:', error);
      return null;
    }
  },

  // ==================== FRANCHISE INFO ====================

  /**
   * Save franchise info
   */
  async saveFranchiseInfo(franchise) {
    try {
      await localDB.put('franchise_info', franchise);
      console.log('[Storage] Saved franchise info:', franchise.id);
      return true;
    } catch (error) {
      console.error('[Storage] Error saving franchise info:', error);
      return false;
    }
  },

  /**
   * Get franchise info
   */
  async getFranchiseInfo(franchiseId) {
    try {
      return await localDB.get('franchise_info', franchiseId);
    } catch (error) {
      console.error('[Storage] Error getting franchise info:', error);
      return null;
    }
  },

  // ==================== LEDGER PARTIES ====================

  /**
   * Save ledger parties
   */
  async saveLedgerParties(parties, franchiseId) {
    try {
      // Add franchise_id to each party for indexing
      const partiesWithFranchise = parties.map(p => ({
        ...p,
        franchise_id: franchiseId
      }));
      await localDB.bulkPut('ledger_parties', partiesWithFranchise);
      console.log(`[Storage] Saved ${parties.length} ledger parties`);
      return true;
    } catch (error) {
      console.error('[Storage] Error saving ledger parties:', error);
      return false;
    }
  },

  /**
   * Get ledger parties for franchise
   */
  async getLedgerParties(franchiseId) {
    try {
      const parties = await localDB.getByIndex('ledger_parties', 'franchise_id', franchiseId);
      console.log(`[Storage] Retrieved ${parties.length} ledger parties`);
      return parties;
    } catch (error) {
      console.error('[Storage] Error getting ledger parties:', error);
      return [];
    }
  },

  /**
   * Update ledger party
   */
  async updateLedgerParty(party) {
    try {
      await localDB.put('ledger_parties', party);
      return true;
    } catch (error) {
      console.error('[Storage] Error updating ledger party:', error);
      return false;
    }
  },

  // ==================== PENDING ORDERS ====================

  /**
   * Save order to pending queue (offline order)
   */
  async savePendingOrder(order) {
    try {
      const orderWithTimestamp = {
        ...order,
        created_at: new Date().toISOString(),
        status: 'pending_sync'
      };
      await localDB.put('pending_orders', orderWithTimestamp);
      console.log('[Storage] Saved pending order:', order.order_no);
      return true;
    } catch (error) {
      console.error('[Storage] Error saving pending order:', error);
      return false;
    }
  },

  /**
   * Get all pending orders
   */
  async getPendingOrders(franchiseId = null) {
    try {
      if (franchiseId) {
        return await localDB.getByIndex('pending_orders', 'franchise_id', franchiseId);
      }
      return await localDB.getAll('pending_orders');
    } catch (error) {
      console.error('[Storage] Error getting pending orders:', error);
      return [];
    }
  },

  /**
   * Delete pending order (after successful sync)
   */
  async deletePendingOrder(orderNo) {
    try {
      await localDB.delete('pending_orders', orderNo);
      console.log('[Storage] Deleted pending order:', orderNo);
      return true;
    } catch (error) {
      console.error('[Storage] Error deleting pending order:', error);
      return false;
    }
  },

  /**
   * Get pending orders count
   */
  async getPendingOrdersCount() {
    try {
      return await localDB.count('pending_orders');
    } catch (error) {
      console.error('[Storage] Error counting pending orders:', error);
      return 0;
    }
  },

  // ==================== SYNCED ORDERS (HISTORY) ====================

  /**
   * Save synced order to history (keep last 100)
   */
  async saveSyncedOrder(order) {
    try {
      await localDB.put('synced_orders', order);
      
      // Keep only last 100 orders
      const allOrders = await localDB.getAll('synced_orders');
      if (allOrders.length > 100) {
        // Sort by date and remove oldest
        allOrders.sort((a, b) => new Date(b.date_timestamp) - new Date(a.date_timestamp));
        const toDelete = allOrders.slice(100);
        for (const order of toDelete) {
          await localDB.delete('synced_orders', order.order_no);
        }
      }
      
      return true;
    } catch (error) {
      console.error('[Storage] Error saving synced order:', error);
      return false;
    }
  },

  /**
   * Get synced orders (history)
   */
  async getSyncedOrders(franchiseId = null) {
    try {
      const orders = await localDB.getAll('synced_orders');
      if (franchiseId) {
        return orders.filter(o => o.franchise_id === franchiseId);
      }
      return orders;
    } catch (error) {
      console.error('[Storage] Error getting synced orders:', error);
      return [];
    }
  },

  // ==================== PENDING LEDGER TRANSACTIONS ====================

  /**
   * Save pending ledger transaction
   */
  async savePendingLedgerTransaction(transaction) {
    try {
      const txWithTimestamp = {
        ...transaction,
        created_at: new Date().toISOString(),
        status: 'pending_sync'
      };
      await localDB.put('pending_ledger', txWithTimestamp);
      console.log('[Storage] Saved pending ledger transaction:', transaction.transaction_id);
      return true;
    } catch (error) {
      console.error('[Storage] Error saving pending ledger transaction:', error);
      return false;
    }
  },

  /**
   * Get pending ledger transactions
   */
  async getPendingLedgerTransactions(partyId = null) {
    try {
      if (partyId) {
        return await localDB.getByIndex('pending_ledger', 'party_id', partyId);
      }
      return await localDB.getAll('pending_ledger');
    } catch (error) {
      console.error('[Storage] Error getting pending ledger transactions:', error);
      return [];
    }
  },

  /**
   * Delete pending ledger transaction
   */
  async deletePendingLedgerTransaction(transactionId) {
    try {
      await localDB.delete('pending_ledger', transactionId);
      console.log('[Storage] Deleted pending ledger transaction:', transactionId);
      return true;
    } catch (error) {
      console.error('[Storage] Error deleting pending ledger transaction:', error);
      return false;
    }
  },

  // ==================== SYNC METADATA ====================

  /**
   * Set sync metadata
   */
  async setSyncMetadata(key, value) {
    try {
      await localDB.put('sync_metadata', { key, value, updated_at: new Date().toISOString() });
      return true;
    } catch (error) {
      console.error('[Storage] Error setting sync metadata:', error);
      return false;
    }
  },

  /**
   * Get sync metadata
   */
  async getSyncMetadata(key) {
    try {
      const data = await localDB.get('sync_metadata', key);
      return data ? data.value : null;
    } catch (error) {
      console.error('[Storage] Error getting sync metadata:', error);
      return null;
    }
  },

  /**
   * Get last sync time
   */
  async getLastSyncTime() {
    return await this.getSyncMetadata('last_sync_time');
  },

  /**
   * Set last sync time
   */
  async setLastSyncTime(timestamp = null) {
    const time = timestamp || new Date().toISOString();
    return await this.setSyncMetadata('last_sync_time', time);
  },

  /**
   * Get sync stats
   */
  async getSyncStats() {
    try {
      const pendingOrders = await this.getPendingOrdersCount();
      const pendingLedger = await localDB.count('pending_ledger');
      const lastSync = await this.getLastSyncTime();

      return {
        pending_orders: pendingOrders,
        pending_ledger_transactions: pendingLedger,
        last_sync_time: lastSync,
        total_pending: pendingOrders + pendingLedger
      };
    } catch (error) {
      console.error('[Storage] Error getting sync stats:', error);
      return {
        pending_orders: 0,
        pending_ledger_transactions: 0,
        last_sync_time: null,
        total_pending: 0
      };
    }
  },

  /**
   * Clear all local data (for logout/reset)
   */
  async clearAll() {
    try {
      await localDB.clear('menu_items');
      await localDB.clear('franchise_info');
      await localDB.clear('ledger_parties');
      await localDB.clear('pending_orders');
      await localDB.clear('synced_orders');
      await localDB.clear('pending_ledger');
      await localDB.clear('sync_metadata');
      console.log('[Storage] Cleared all local data');
      return true;
    } catch (error) {
      console.error('[Storage] Error clearing data:', error);
      return false;
    }
  }
};

export default StorageService;
